import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Card,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "../examples/Tables/DataTable";
import CustomPagination from "components/CustomPagination";
import PelangganSearch from "components/STSearch/PelangganSearch";
import EmptyState from "components/EmptyState";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";
import { useMaterialUIController } from "context";
import { handleApiError } from "utils/errorHandler";
import {
  apiCreateCustomer,
  apiDeleteCustomer,
  apiGetCustomerByNokp,
  apiUpdateCustomerByNokp,
} from "api/customers-paginated";
import { apiGetAkaunByNokp, apiUpdateAkaun } from "api/akaun";
import { autoCapitalizePayload } from "utils/autoCapitalize";

// Data
import dataPelanggan from "./data/pelanggan";

export default function MaklumatPelanggan() {
  const { showSuccess, showError, showInfo } = useSnackbar();
  const { confirmDelete } = useConfirmDialog();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState({
    nama: "",
    no_kp: "",
    nama_syarikat: "",
  });
  const [openTambah, setOpenTambah] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [originalNoKpRef, setOriginalNoKpRef] = useState("");
  const debounceRef = useRef();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed to 10 items per page

  const [controller] = useMaterialUIController();
  const { darkMode, whiteSidenav, transparentSidenav } = controller;
  const shouldApplyStyle =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  function handleRowSelect(row) {
    if (!row) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow((prev) => (prev && prev.id === row.id ? null : row));
  }

  // All state declarations above

  // Get table data using the new data hook (must be after all state declarations)
  const { columns, rows, pagination, loading: dataLoading } = dataPelanggan({
    onRowSelect: handleRowSelect,
    selectedRow,
    currentPage,
    itemsPerPage,
    search,
    refreshTrigger,
  });

  // Combine loading states
  const isLoading = loading || dataLoading;

  const handleSearchChange = (e) => {
    setSearch({ ...search, [e.target.name]: e.target.value });
    setCurrentPage(1); // Reset to first page when searching
  };

  // Debounced refresh when search changes, but skip on initial mount if all fields are empty (to match Senarai Penyewa)
  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // On first load, skip refresh if all fields are empty
    const allEmpty = !search.nama && !search.no_kp && !search.nama_syarikat;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      if (allEmpty) return;
    }
    debounceRef.current = setTimeout(() => {
      setRefreshTrigger((prev) => prev + 1);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search.nama, search.no_kp, search.nama_syarikat]);

  const handleTambahSave = async (data) => {
    try {
      setLoading(true);
      const payload = autoCapitalizePayload(data);
      await apiCreateCustomer(payload);
      setRefreshTrigger((prev) => prev + 1);
      setOpenTambah(false);
      showSuccess("Pelanggan berjaya ditambah");
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    if (selectedRow) {
      setOriginalNoKpRef(selectedRow.no_kp || "");
      setOpenEdit(true);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) {
      showInfo("Sila pilih pelanggan untuk dipadam");
      return;
    }

    const customerName = selectedRow.nama || "pelanggan ini";
    const confirmed = await confirmDelete(customerName);
    
    if (!confirmed) return;

    try {
      setLoading(true);
      await apiDeleteCustomer(selectedRow.no_kp);
      setRefreshTrigger((prev) => prev + 1);
      setSelectedRow(null);
      showSuccess("Pelanggan berjaya dipadam");
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers (fix double refresh)
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Filter logic is now handled in the data hook
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.special}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Maklumat Pelanggan
                </MDTypography>
              </MDBox>
              <MDBox>
                <PelangganSearch
                  search={search}
                  handleSearchChange={handleSearchChange}
                  onEdit={handleEditClick}
                  onDelete={handleDelete}
                  selectedRow={selectedRow}
                  loading={isLoading}
                />
                
                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                  </Box>
                ) : rows.length === 0 ? (
                  <EmptyState
                    title="Tiada Pelanggan Dijumpai"
                    description={
                      search.nama || search.no_kp || search.nama_syarikat
                        ? "Cuba carian lain atau tambah pelanggan baharu"
                        : "Tiada data pelanggan. Klik butang 'Tambah' untuk menambah pelanggan baharu."
                    }
                  />
                ) : (
                  <>
                    <DataTable
                      table={{ columns, rows }}
                      showTotalEntries={false}
                      isSorted={false}
                      noEndBorder
                      entriesPerPage={false}
                      canSearch={false}
                    />
                    <CustomPagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      totalItems={pagination.totalItems}
                      itemsPerPage={itemsPerPage}
                      actualItemsOnPage={pagination.actualItemsOnPage}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPrevPage={handlePrevPage}
                      variant="contained"
                      color="info"
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
